// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - FounderCountdown
// Compteur animé de places Fondateur restantes avec effet d'urgence
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Users, Flame, Clock, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// Animated Digit Component
// ═══════════════════════════════════════════════════════════════════════════════

function AnimatedDigit({ digit, delay = 0 }) {
  return (
    <div className="relative overflow-hidden h-[1.2em] w-[0.65em]">
      <AnimatePresence mode="popLayout">
        <motion.span
          key={digit}
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 30,
            delay 
          }}
          className="absolute inset-0 flex items-center justify-center font-mono"
        >
          {digit}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Animated Number Component
// ═══════════════════════════════════════════════════════════════════════════════

function AnimatedNumber({ value, className = '' }) {
  const digits = String(value).split('');
  
  return (
    <div className={`flex items-center justify-center ${className}`}>
      {digits.map((digit, i) => (
        <AnimatedDigit 
          key={`${i}-${digit}`} 
          digit={digit} 
          delay={i * 0.05}
        />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function FounderCountdown({ 
  variant = 'banner', // 'banner' | 'compact' | 'full'
  initialCount = 847,
  totalSpots = 1000,
  className = ''
}) {
  const [count, setCount] = useState(initialCount);
  const [isUrgent, setIsUrgent] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [recentlyDecremented, setRecentlyDecremented] = useState(false);
  const intervalRef = useRef(null);
  
  // Fetch real count from API
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/abracacoins/plans`);
        if (response.ok) {
          const data = await response.json();
          if (data.founder_offer?.remaining_spots) {
            setCount(data.founder_offer.remaining_spots);
          }
        }
      } catch (err) {
        // Use initial count on error
      }
    };
    fetchCount();
  }, []);
  
  // Simulate occasional decrements for urgency (demo mode)
  useEffect(() => {
    // Random interval between 15-45 seconds
    const scheduleNextDecrement = () => {
      const delay = 15000 + Math.random() * 30000;
      intervalRef.current = setTimeout(() => {
        setCount(prev => {
          if (prev > 100) {
            setRecentlyDecremented(true);
            setTimeout(() => setRecentlyDecremented(false), 3000);
            return prev - 1;
          }
          return prev;
        });
        scheduleNextDecrement();
      }, delay);
    };
    
    scheduleNextDecrement();
    
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, []);
  
  // Update urgency status
  useEffect(() => {
    setIsUrgent(count <= 200);
    setShowPulse(count <= 100);
  }, [count]);
  
  // Pourcentage restant
  const percentage = Math.round((count / totalSpots) * 100);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // VARIANT: COMPACT (pour header ou sidebar)
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (variant === 'compact') {
    return (
      <motion.div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
          isUrgent 
            ? 'bg-red-100 text-red-700' 
            : 'bg-emerald-100 text-emerald-700'
        } ${className}`}
        animate={showPulse ? { scale: [1, 1.05, 1] } : {}}
        transition={{ duration: 1, repeat: showPulse ? Infinity : 0 }}
      >
        <Crown size={14} className={isUrgent ? 'text-red-500' : 'text-emerald-500'} />
        <span className="text-sm font-bold">
          <AnimatedNumber value={count} />
        </span>
        <span className="text-xs opacity-70">places</span>
      </motion.div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // VARIANT: BANNER (pour la page tarifs)
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (variant === 'banner') {
    return (
      <div className={`relative ${className}`}>
        {/* Effet pulse d'urgence */}
        <AnimatePresence>
          {recentlyDecremented && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.2 }}
              className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
            >
              <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                -1 place à l'instant !
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.div
          className="text-center"
          animate={recentlyDecremented ? { scale: [1, 1.1, 1] } : {}}
          transition={{ duration: 0.3 }}
        >
          <div className={`text-4xl md:text-5xl font-black tabular-nums ${
            isUrgent ? 'text-red-400' : 'text-white'
          }`}>
            <AnimatedNumber value={count} />
          </div>
          <p className="text-sm text-white/70 mt-1">places restantes</p>
          
          {/* Barre de progression */}
          <div className="mt-3 w-32 mx-auto">
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${
                  isUrgent ? 'bg-red-400' : 'bg-emerald-400'
                }`}
                initial={{ width: '100%' }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-xs text-white/50 mt-1">{percentage}% restant</p>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // VARIANT: FULL (widget complet avec détails)
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <motion.div
      className={`bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white relative overflow-hidden ${className}`}
      animate={showPulse ? { boxShadow: ['0 0 0 0 rgba(16,185,129,0)', '0 0 0 8px rgba(16,185,129,0.3)', '0 0 0 0 rgba(16,185,129,0)'] } : {}}
      transition={{ duration: 2, repeat: showPulse ? Infinity : 0 }}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-white/20 rounded-lg">
          <Crown size={24} className="text-yellow-300" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Offre Membre Fondateur</h3>
          <p className="text-white/70 text-sm">Double tes AbracaCoins — à vie</p>
        </div>
      </div>
      
      {/* Counter */}
      <div className="text-center py-6 relative">
        <AnimatePresence>
          {recentlyDecremented && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute -top-2 left-1/2 -translate-x-1/2"
            >
              <span className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                <Flame size={12} />
                Quelqu'un vient de s'inscrire !
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.div
          animate={recentlyDecremented ? { scale: [1, 1.15, 1] } : {}}
          transition={{ duration: 0.4 }}
        >
          <div className={`text-6xl font-black tabular-nums ${isUrgent ? 'text-red-300' : ''}`}>
            <AnimatedNumber value={count} />
          </div>
        </motion.div>
        <p className="text-white/70 mt-1">places restantes sur {totalSpots.toLocaleString()}</p>
      </div>
      
      {/* Progress bar */}
      <div className="mb-4">
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${isUrgent ? 'bg-red-400' : 'bg-yellow-300'}`}
            initial={{ width: '100%' }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-white/60">
          <span>0</span>
          <span>{percentage}% restant</span>
          <span>{totalSpots.toLocaleString()}</span>
        </div>
      </div>
      
      {/* Urgency message */}
      <AnimatePresence>
        {isUrgent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 p-3 bg-red-500/30 rounded-lg mt-4"
          >
            <AlertTriangle size={18} className="text-red-300 flex-shrink-0" />
            <p className="text-sm">
              <strong>Attention !</strong> Il reste moins de 200 places. 
              L'offre Fondateur ne sera plus disponible après.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/20">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-yellow-300">
            <Users size={16} />
            <span className="font-bold">{totalSpots - count}</span>
          </div>
          <p className="text-xs text-white/60">Fondateurs inscrits</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-yellow-300">
            <Clock size={16} />
            <span className="font-bold">x2</span>
          </div>
          <p className="text-xs text-white/60">Bonus à vie</p>
        </div>
      </div>
    </motion.div>
  );
}

// Export des variantes
export { AnimatedNumber, AnimatedDigit };
